import { useCallback, useEffect, useRef } from "preact/hooks";
import { useComputed, useSignal, useSignalEffect } from "@preact/signals";
import { useLocal } from "../useLocal";
import { ResizeHandlerBehavior, ResizeHandlerParams } from "./interfaces";

const useResizeHandler = ({
    min = 300,
    max = 400,
    defaultValue = typeof min === "number" ? min : min(),
    storageKey = "v1_rh_tmp",
    behavior = ResizeHandlerBehavior.Standard,
}: ResizeHandlerParams) => {
    const settings = useLocal(storageKey, {
        width: defaultValue,
    });
    const isResizing = useSignal(false);
    const resizeThumb = useRef<HTMLButtonElement>(null);
    const resizeData = useRef({
        startX: 0,
    });
    const width = useComputed(() => settings.value.width);

    useSignalEffect(() => {
        document.body.style.userSelect = isResizing.value ? "none" : "";
    });

    useEffect(() => {
        let tmId = 0;

        const handleMouseMove = (e: MouseEvent) => {
            if (!isResizing.peek() || !resizeThumb.current) {
                return;
            }

            const { startX } = resizeData.current;
            const delta = (e.clientX - startX) * behavior;
            const newWidth = settings.peek().width + delta;
            const mn = typeof min === "number" ? min : min();
            const mx = typeof max === "number" ? max : max();

            if (newWidth < mn || newWidth > mx) {
                return;
            }

            resizeData.current.startX = e.clientX;
            settings.value = {
                width: newWidth,
            };
        };

        const handleMouseUp = () => {
            if (isResizing.peek()) {
                isResizing.value = false;
            }
        };

        const handleResize = () => {
            if (isResizing.peek()) {
                isResizing.value = false;
            }

            const mn = typeof min === "number" ? min : min();
            const mx = typeof max === "number" ? max : max();
            const currentWidth = settings.peek().width;

            if (currentWidth < mn || currentWidth > mx) {
                settings.value = {
                    width: Math.max(mn, Math.min(mx, currentWidth)),
                };
            }
        };

        const handleKeyDown = (e: KeyboardEvent) => {
            const active = document.activeElement === resizeThumb.current;

            if (!active) {
                return;
            }

            const mn = typeof min === "number" ? min : min();
            const mx = typeof max === "number" ? max : max();

            let factor = 0;
            let newWidth = settings.peek().width;

            if (e.key === "ArrowLeft") {
                factor = -4;
            } else if (e.key === "ArrowRight") {
                factor = 4;
            } else if (e.key === "Home") {
                newWidth = mn;
            } else if (e.key === "End") {
                newWidth = mx;
            }

            if (factor !== 0) {
                newWidth += factor * behavior * (e.shiftKey ? 10 : 1);
            }

            if (newWidth !== settings.peek().width) {
                e.preventDefault();

                if (newWidth < mn) {
                    newWidth = mn;
                } else if (newWidth > mx) {
                    newWidth = mx;
                }

                isResizing.value = true;
                settings.value = {
                    width: newWidth,
                };

                // isResizing needs to be marked for a moment
                // to make sidebar resize instant, not transitioned
                //
                window.clearTimeout(tmId);
                tmId = window.setTimeout(() => {
                    isResizing.value = false;
                }, 50);
            }
        };

        window.addEventListener("mousemove", handleMouseMove);
        window.addEventListener("mouseup", handleMouseUp);
        window.addEventListener("pointerup", handleMouseUp);
        window.addEventListener("keydown", handleKeyDown);
        window.addEventListener("resize", handleResize);

        return () => {
            window.removeEventListener("mousemove", handleMouseMove);
            window.removeEventListener("mouseup", handleMouseUp);
            window.removeEventListener("pointerup", handleMouseUp);
            window.removeEventListener("keydown", handleKeyDown);
            window.removeEventListener("resize", handleResize);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [behavior]);

    const handleStartResize = useCallback(
        (e: MouseEvent) => {
            resizeData.current.startX = e.clientX;
            isResizing.value = true;
        },
        [isResizing],
    );

    return {
        settings,
        width,
        isResizing,
        resizeThumb,
        handleStartResize,
    };
};

export default useResizeHandler;
